import React from 'react';
import { Loading } from 'design-system-web';
import cx from 'classnames';
import { useChannelAdminContext } from 'hooks';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';

import { routes } from 'lane-shared/config';
import { useUpdateCategory, UpdateCategoryError } from './hooks/updateCategory';
import { useProductCategory } from './hooks/useProductCategory';
import { CategoryForm, CategoryFormData } from './CategoryForm';

import { AdminPage, PageHeader } from 'components/layout';

import styles from '../styles.scss';

type Props = {
  breadcrumbs?: {
    label: string;
    url?: string;
  }[];
  className?: string;
};

function resolveFormError(
  updateCategoryError: unknown,
  error: Error | undefined
): { severity: 'warning' | 'error'; error: Error } | undefined {
  if (updateCategoryError instanceof Error) {
    return {
      severity:
        updateCategoryError instanceof UpdateCategoryError ? 'warning' : 'error',
      error: updateCategoryError,
    };
  }

  if (error) {
    return { severity: 'error', error };
  }

  return undefined;
}

export function EditCategoryForm({ breadcrumbs, className }: Props) {
  const history = useHistory();
  const { channel } = useChannelAdminContext();
  const { categoryId }: { categoryId: string } = useParams();
  const { t } = useTranslation();

  const channel_slug = channel?.slug || '';
  const productServicesLink = routes.channelAdminProductsServices.replace(
    ':id',
    channel_slug
  );

  const defaultBreadcrumbs = [
    {
      label: t('adp.productsServices.breadcrumb'),
      url: productServicesLink,
    },
    { label: t('abp.productsServices.editCategory.breadcrumb') },
  ];

  const {
    updateCategory,
    loading: updating,
    error: updateCategoryError,
  } = useUpdateCategory();

  const { productCategory, loading, error } = useProductCategory({
    productCategoryId: categoryId,
    groupId: channel?._id,
    skip: !categoryId || !channel?._id,
  });

  const updateCategoryDetails = async (data: CategoryFormData) => {
    if (!channel?._id) {
      window.Toast.show(t('abp.productsServices.editCategory.channelNotAvailable'), 'error');

      return;
    }

    try {
      await updateCategory({
        productCategoryId: categoryId,
        name: data.name,
        description: data.description,
        channelId: channel._id,
      });

      history.goBack();
      window.Toast.show(t('abp.productsServices.editCategory.successToast'));
    } catch(error) {
      console.error('Failed to update category', error); 
    } 
  };

  const handleCancel = () => history.goBack();

  const formError = resolveFormError(updateCategoryError, error);

  if (loading && !productCategory) {
  return (
    <AdminPage className={className}>
      <PageHeader
        header={t('abp.productsServices.editCategory.heading')}
        headerLevel="h3"
        breadcrumbs={breadcrumbs ?? defaultBreadcrumbs}
      />
      <div className={styles.centeredContent}>
        <Loading />
      </div>
    </AdminPage>
  );
}

  return (
    <AdminPage className={className}>
      <PageHeader
        externalPadding={[0, 0]}
        header={t('abp.productsServices.editCategory.heading')}
        headerLevel="h3"
        breadcrumbs={breadcrumbs ?? defaultBreadcrumbs}
      />
      <div className={cx(styles.editCategoryForm)}>
        <div className={styles.fieldset}>
          <label className={styles.editLabel}>
            {t('abp.productsServices.editCategory.type')}
          </label>
          <div className={styles.typeValue}>
            {productCategory?.productType &&
              t(
                `abp.productsServices.productType.${productCategory.productType}`
              )}
          </div>
        </div>
        <CategoryForm
          productCategory={productCategory}
          error={formError}
          disabled={updating}
          onSubmit={updateCategoryDetails}
          onCancel={handleCancel}
        />
      </div>
    </AdminPage>
  );
}
